import * as fs from 'node:fs';
import * as path from 'node:path';
import type { ServerLog } from '../types';

/**
 * Log level mapping from Pino numeric levels
 */
function mapPinoLevelToServerLogLevel(
  pinoLevel: number | string
): 'fatal' | 'error' | 'warn' | 'log' | 'debug' | 'verbose' {
  if (typeof pinoLevel === 'string') {
    const lower = pinoLevel.toLowerCase();
    if (lower === 'fatal') return 'fatal';
    if (lower === 'error') return 'error';
    if (lower === 'warn' || lower === 'warning') return 'warn';
    if (lower === 'info' || lower === 'log') return 'log';
    if (lower === 'debug') return 'debug';
    if (lower === 'trace' || lower === 'verbose') return 'verbose';
    return 'log';
  }

  if (pinoLevel >= 60) return 'fatal';
  if (pinoLevel >= 50) return 'error';
  if (pinoLevel >= 40) return 'warn';
  if (pinoLevel >= 30) return 'log';
  if (pinoLevel >= 20) return 'debug';
  return 'verbose';
}

/**
 * Extract log level from text content
 */
function extractLogLevel(text: string): 'fatal' | 'error' | 'warn' | 'log' | 'debug' | 'verbose' {
  const lower = text.toLowerCase();

  if (lower.includes('fatal') || lower.includes('critical')) return 'fatal';
  if (lower.includes('error') || lower.includes('<e>') || lower.includes('✖')) return 'error';
  if (lower.includes('warn') || lower.includes('warning') || lower.includes('<w>') || lower.includes('⚠')) return 'warn';
  if (lower.includes('debug') || lower.includes('<d>')) return 'debug';
  if (lower.includes('verbose') || lower.includes('trace')) return 'verbose';

  return 'log';
}

/**
 * Generate a simple UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Parse Pino JSON log to ServerLog format
 */
function parsePinoLog(line: string, source: 'server' | 'trace'): ServerLog | null {
  try {
    const pinoLog = JSON.parse(line);
    const id = generateUUID();

    return {
      id,
      level: mapPinoLevelToServerLogLevel(pinoLog.level),
      timestamp: new Date(pinoLog.time).getTime(),
      message: pinoLog.message || pinoLog.msg || '',
      context: pinoLog.context || null,
      traceId: pinoLog.trace_id || null,
      userId: pinoLog.user_id || null,
      appId: pinoLog.app_id || null,
      tenantId: pinoLog.tenant_id || null,
      stack: pinoLog.stack || null,
      meta: {
        pid: pinoLog.pid,
        hostname: pinoLog.hostname,
        path: pinoLog.path,
        method: pinoLog.method,
        statusCode: pinoLog.status_code,
        durationMs: pinoLog.duration_ms,
        ip: pinoLog.ip,
        pageRoute: pinoLog.page_route,
        requestBody: pinoLog.request_body,
        responseBody: pinoLog.response_body,
        queryParams: pinoLog.query_params,
      },
      tags: [source],
    };
  } catch {
    return null;
  }
}

/**
 * Parse plain text log to ServerLog format
 */
function parseStdLog(line: string, source: 'server-std' | 'client-std'): ServerLog | null {
  const id = generateUUID();
  const match = line.match(/^\[(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\] \[(server|client)\] (.*)$/);

  if (!match) {
    return {
      id,
      level: extractLogLevel(line),
      timestamp: Date.now(),
      message: line,
      context: null,
      traceId: null,
      userId: null,
      appId: null,
      tenantId: null,
      stack: null,
      meta: null,
      tags: [source],
    };
  }

  const [, timeStr, , content] = match;
  let timestamp: number;

  try {
    const isoStr = timeStr.replace(' ', 'T');
    timestamp = new Date(isoStr).getTime();
    if (isNaN(timestamp)) {
      timestamp = Date.now();
    }
  } catch {
    timestamp = Date.now();
  }

  return {
    id,
    level: extractLogLevel(content),
    timestamp,
    message: content,
    context: null,
    traceId: null,
    userId: null,
    appId: null,
    tenantId: null,
    stack: null,
    meta: null,
    tags: [source],
  };
}

export type LogSubscriber = (log: ServerLog) => void;

interface LogFileConfig {
  fileName: string;
  source: string;
  parser: (line: string, source: string) => ServerLog | null;
}

/**
 * LogWatcher - watches log files and emits new log entries via SSE
 */
export class LogWatcher {
  private logDir: string = '';
  private watchers: Map<string, fs.FSWatcher> = new Map();
  private filePositions: Map<string, number> = new Map();
  private subscribers: Set<LogSubscriber> = new Set();
  private isRunning: boolean = false;
  private debug: boolean = false;

  private readonly logFiles: LogFileConfig[] = [
    { fileName: 'server.log', source: 'server', parser: parsePinoLog as (line: string, source: string) => ServerLog | null },
    { fileName: 'trace.log', source: 'trace', parser: parsePinoLog as (line: string, source: string) => ServerLog | null },
    { fileName: 'server.std.log', source: 'server-std', parser: parseStdLog as (line: string, source: string) => ServerLog | null },
    { fileName: 'client.std.log', source: 'client-std', parser: parseStdLog as (line: string, source: string) => ServerLog | null },
  ];

  constructor(options: { debug?: boolean } = {}) {
    this.debug = options.debug ?? false;
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[LogWatcher]', ...args);
    }
  }

  /**
   * Start watching log files
   */
  start(logDir: string): void {
    if (this.isRunning) {
      this.log('Already running, ignoring start call');
      return;
    }

    this.logDir = logDir;
    this.isRunning = true;
    this.log(`Starting to watch log files in: ${logDir}`);

    for (const config of this.logFiles) {
      this.watchFile(config);
    }
  }

  /**
   * Stop watching all files
   */
  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.log('Stopping file watchers');
    this.isRunning = false;

    Array.from(this.watchers.entries()).forEach(([fileName, watcher]) => {
      watcher.close();
      this.log(`Closed watcher for: ${fileName}`);
    });

    this.watchers.clear();
    this.filePositions.clear();
  }

  /**
   * Subscribe to new log events
   */
  onLog(callback: LogSubscriber): () => void {
    this.subscribers.add(callback);
    this.log(`Subscriber added, total: ${this.subscribers.size}`);

    return () => {
      this.subscribers.delete(callback);
      this.log(`Subscriber removed, total: ${this.subscribers.size}`);
    };
  }

  /**
   * Get subscriber count
   */
  getSubscriberCount(): number {
    return this.subscribers.size;
  }

  /**
   * Watch a single log file
   */
  private watchFile(config: LogFileConfig): void {
    const filePath = path.join(this.logDir, config.fileName);

    // Check if file exists
    if (!fs.existsSync(filePath)) {
      this.log(`File not found, skipping: ${config.fileName}`);
      return;
    }

    // Initialize file position to end of file (only watch new content)
    try {
      const stats = fs.statSync(filePath);
      this.filePositions.set(config.fileName, stats.size);
      this.log(`Initialized position for ${config.fileName}: ${stats.size} bytes`);
    } catch (error) {
      this.log(`Failed to get initial position for ${config.fileName}:`, error);
      this.filePositions.set(config.fileName, 0);
    }

    // Start watching
    try {
      const watcher = fs.watch(filePath, (eventType) => {
        if (eventType === 'change') {
          this.handleFileChange(config);
        }
      });

      watcher.on('error', (error) => {
        this.log(`Watcher error for ${config.fileName}:`, error);
        // Try to restart watcher
        this.restartWatcher(config);
      });

      this.watchers.set(config.fileName, watcher);
      this.log(`Started watching: ${config.fileName}`);
    } catch (error) {
      this.log(`Failed to start watcher for ${config.fileName}:`, error);
    }
  }

  /**
   * Restart a file watcher after error
   */
  private restartWatcher(config: LogFileConfig): void {
    const existingWatcher = this.watchers.get(config.fileName);
    if (existingWatcher) {
      existingWatcher.close();
      this.watchers.delete(config.fileName);
    }

    // Wait a bit before restarting
    setTimeout(() => {
      if (this.isRunning) {
        this.log(`Restarting watcher for: ${config.fileName}`);
        this.watchFile(config);
      }
    }, 1000);
  }

  /**
   * Handle file change event - read new content
   */
  private handleFileChange(config: LogFileConfig): void {
    const filePath = path.join(this.logDir, config.fileName);
    const lastPosition = this.filePositions.get(config.fileName) || 0;

    try {
      const stats = fs.statSync(filePath);
      const currentSize = stats.size;

      // File was truncated/rotated - reset position
      if (currentSize < lastPosition) {
        this.log(`File ${config.fileName} was truncated, resetting position`);
        this.filePositions.set(config.fileName, 0);
        this.handleFileChange(config); // Re-read from beginning
        return;
      }

      // No new content
      if (currentSize === lastPosition) {
        return;
      }

      // Read new content
      const readSize = currentSize - lastPosition;
      const buffer = Buffer.alloc(readSize);
      const fd = fs.openSync(filePath, 'r');

      try {
        fs.readSync(fd, buffer, 0, readSize, lastPosition);
      } finally {
        fs.closeSync(fd);
      }

      // Update position
      this.filePositions.set(config.fileName, currentSize);

      // Parse lines
      const content = buffer.toString('utf8');
      const lines = content.split('\n');

      for (const line of lines) {
        if (!line.trim()) continue;

        try {
          const log = config.parser(line, config.source);
          if (log) {
            this.notifySubscribers(log);
          }
        } catch {
          // Ignore parse errors for individual lines
        }
      }
    } catch (error) {
      this.log(`Error reading file ${config.fileName}:`, error);
    }
  }

  /**
   * Notify all subscribers of new log
   */
  private notifySubscribers(log: ServerLog): void {
    Array.from(this.subscribers).forEach(subscriber => {
      try {
        subscriber(log);
      } catch (error) {
        this.log('Subscriber error:', error);
      }
    });
  }
}
